import { Button, Center, Divider, Text, VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { DetailField } from '../../../components/DetailField'
import { LoadingBlock } from '../../../components/Loading'
import { useAppToast } from '../../../helpers'
import ExperienceFeedbackService from '../../../services/ExperienceFeedback/ExperienceFeedbackService'
import {
  IExperienceFeedback,
  IExperienceFeedbackSummary,
  IQuestion
} from '../../../services/ExperienceFeedback/interfaces'
import { ISector } from '../../../services/Sector/SectorService'
import ExperienceFeedbackScore from './ExperienceFeedbackScore'

interface IAnswer {
  id: number
  value: string
  question_id: number
  request_id: number
}

export default function ExperienceFeedbackViewPage({
  experienceFeedbackId: id,
  sectors
}: {
  experienceFeedbackId: number
  sectors: ISector[]
}) {
  const experienceFeedbackService = useMemo(() => new ExperienceFeedbackService(), [])
  const [experienceFeedback, setExperienceFeedback] = useState<IExperienceFeedback>()
  const [answers, setAnswers] = useState<IAnswer[]>([])
  const [questions, setQuestions] = useState<IQuestion[]>([])
  const [summary, setSummary] = useState<IExperienceFeedbackSummary>()
  const toast = useAppToast()

  const fetchExperienceFeedback = async () => {
    try {
      const response = await experienceFeedbackService.getAnswers(id)
      setExperienceFeedback(response.data.experience_feedback)
      setAnswers(response.data.answers)
      setQuestions(response.data.questions)

      const { score, max_score: maxScore, result } = response.data
      // Loose check on purpose: the API response is untyped at runtime and may omit these.
      if (score != null && maxScore != null && result != null) {
        setSummary({ score, max_score: maxScore, result })
      }
    } catch {
      toast.error('Erro ao buscar informações da Avaliação de Experiência!')
    }
  }

  useEffect(() => {
    fetchExperienceFeedback()
  }, [])

  return (
    <>
      {!experienceFeedback ? (
        <LoadingBlock size="page" />
      ) : (
        <>
          <VStack align="stretch" spacing={4}>
            <DetailField label="Nome do liderado" value={experienceFeedback.name} emptyValue="Sem resposta" />
            <DetailField label="E-mail do liderado" value={experienceFeedback.email} emptyValue="Sem resposta" />
            <DetailField
              label="Setor"
              value={sectors.find((sector) => sector.id === experienceFeedback.sector_id)?.name}
              emptyValue="Sem resposta"
            />
            <DetailField label="Gestor" value={experienceFeedback.manager} emptyValue="Sem resposta" />
          </VStack>

          <Divider my={6} />

          <VStack align="stretch" spacing={4}>
            {questions.map((question) => {
              const answerValue = answers.find((answer) => answer.question_id === question.id)?.value

              return (
                <DetailField key={question.id} label={question.title}>
                  {question.description && <Text whiteSpace="pre-line">{question.description}</Text>}
                  {question.type === 'options' && (
                    <Center>
                      <Button colorScheme="magenta" my={4}>
                        {answerValue || 'Sem resposta'}
                      </Button>
                    </Center>
                  )}
                  {question.type === 'textarea' && <Text>{answerValue || 'Sem resposta'}</Text>}
                </DetailField>
              )
            })}
          </VStack>

          {summary && <ExperienceFeedbackScore {...summary} />}
        </>
      )}
    </>
  )
}
